import {
  computed,
  inject,
  Injectable,
  OnDestroy,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Notification, NotificationData } from '@repo/models';
import { createClient, RealtimeChannel, SupabaseClient } from '@supabase/supabase-js';
import { asyncScheduler, ReplaySubject, scheduled } from 'rxjs';
import { environment } from 'src/environments/environment';
import { UnauToastConfig } from 'src/services/toast/toast.model';
import { ToastService } from 'src/services/toast/toast.service';
import { PaymentService } from './payment.service';

export enum NotificationType {
  PAYMENT = 'PAYMENT',
  CALL = 'CALL',
  COMPANION_CONNECTION_LOST = 'COMPANION_CONNECTION_LOST',
  POS_ORDER_SYNC_FAILED = 'POS_ORDER_SYNC_FAILED',
}

export enum NotificationStatus {
  UNREAD = 'UNREAD',
  DONE = 'DONE',
}

@Injectable({
  providedIn: 'root',
})
export class NotificationService implements OnDestroy {
  private hasNewNotification$ = new ReplaySubject<Notification>(1);
  private notificationSubscriptionChannel: RealtimeChannel | null = null;
  private databaseClient: SupabaseClient | null;
  private notifications: WritableSignal<Notification[]> = signal<Notification[]>([]);
  private unresolvedNotiCount = computed(
    () => this.notifications().filter((noti) => noti.status !== NotificationStatus.DONE).length
  );

  private readonly translateService = inject(TranslateService);
  private readonly paymentService = inject(PaymentService);
  private readonly toastService = inject(ToastService);

  constructor() {
    this.databaseClient = createClient(
      environment.databaseUrl.supabase,
      environment.databaseAnonKey.supabase
    );
    this.initializeNotifications();
  }

  ngOnDestroy(): void {
    this.hasNewNotification$.complete();
  }

  public getUnresolvedNotificationCount(): Signal<number> {
    return this.unresolvedNotiCount;
  }

  public async getHasNewNotificationSubject(): Promise<ReplaySubject<Notification>> {
    const client = this.getDatabaseClient();
    if (this.notificationSubscriptionChannel != null) {
      this.notificationSubscriptionChannel.unsubscribe();
    }
    this.notificationSubscriptionChannel = client
      .channel('notifications_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
        },
        async (payload) => {
          const hasNewNoti = payload.eventType === 'INSERT';
          const shouldRefresh =
            hasNewNoti ||
            payload.eventType === 'DELETE' ||
            (payload.eventType === 'UPDATE' && (payload.new as Notification).status === 'DONE');

          if (shouldRefresh) {
            this.getEntities().then((notifications) => {
              this.notifications.set(notifications);
              if (hasNewNoti) {
                this.hasNewNotification$.next(notifications[0]);
              }
              return;
            });
          }
        }
      )
      .subscribe();
    return this.hasNewNotification$;
  }

  public async startListeningForNotifications() {
    if (window.location.search.includes('fullscreen=true')) {
      return;
    }
    const hasNewNotification = await this.getHasNewNotificationSubject();
    hasNewNotification.subscribe(async (notification: Notification) => {
      let message = this.translateService.instant('NOTIFICATION.' + notification.type, {
        table: notification.table.name || '',
        tabletId: typeof notification.data === 'object' ? notification.data?.tabletId : undefined,
      });
      let toastFn = (config: UnauToastConfig) => this.toastService.infoToast(config);

      if (notification.type === NotificationType.PAYMENT) {
        message = await this.getPaymentNotificationMsgContent(notification);
      }
      if (notification.type === NotificationType.POS_ORDER_SYNC_FAILED) {
        message = this.getPOSOrderSyncFailedMsgContent(notification);
        toastFn = (config: UnauToastConfig) => this.toastService.errorToast(config);
      }
      if (notification.type === NotificationType.COMPANION_CONNECTION_LOST) {
        toastFn = (config: UnauToastConfig) => this.toastService.warningToast(config);
      }

      const toast = await toastFn({
        header: this.translateService.instant('NOTIFICATION.HEADER'),
        message: message,
        duration: 5000,
      });
    });
  }

  private async getPaymentNotificationMsgContent(notification: Notification) {
    if (notification.type !== NotificationType.PAYMENT || notification.data == null) {
      return '';
    }
    const notificationContent = notification.data as NotificationData;

    return this.translateService.instant('NOTIFICATION.PAYMENT', {
      table: notification.table.name,
      method: this.translateService.instant(
        'PAYMENT.METHOD.' + notificationContent.paymentMethod || ''
      ),
      type: this.translateService.instant('PAYMENT.TYPE.' + notificationContent.paymentType || ''),
    });
  }

  private getPOSOrderSyncFailedMsgContent(latestNoti: Notification) {
    const data = latestNoti.data as any;
    return this.translateService.instant('NOTIFICATION.POS_ORDER_SYNC_FAILED', {
      table: latestNoti.table.name,
      orderId: data?.orderId ?? '-',
    });
  }

  public async getEntities(): Promise<Notification[]> {
    const client = this.getDatabaseClient();
    const { data, error } = await client
      .from('notifications')
      .select(
        `
          id,
          status,
          type,
          data,
          createdAt:created_at,
          updatedAt:updated_at,
          table:tables (
            id,
            name
          )
        `
      )
      .order('created_at', { ascending: false })
      .filter('created_at', 'gte', new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString());
    if (error != null) {
      console.error(error);
      return [];
    }
    return (data as unknown as Notification[]) || [];
  }

  public async updateEntity(uuid: string, status: NotificationStatus): Promise<Notification> {
    return new Promise<Notification>(async (resolve, reject) => {
      const client = this.getDatabaseClient();
      const { data, error } = await client
        .from('notifications')
        .update({ status: status, updated_at: new Date().toISOString() })
        .eq('id', uuid)
        .neq('status', NotificationStatus.DONE);
      if (error != null) {
        console.error(error);
        reject();
      }
      resolve(data as unknown as Notification);
    });
  }

  public getNotifications() {
    return this.notifications.asReadonly();
  }

  private async initializeNotifications() {
    scheduled(this.getEntities(), asyncScheduler).subscribe((notifications: Notification[]) => {
      this.notifications.set(notifications);
    });
  }

  protected getDatabaseClient(): SupabaseClient {
    if (this.databaseClient == null) {
      this.databaseClient = createClient(
        environment.databaseUrl.supabase,
        environment.databaseAnonKey.supabase
      );
    }
    return this.databaseClient;
  }
}
